import React, { FC, FormEventHandler, MouseEventHandler, ReactNode } from 'react'
import { Modal } from 'antd'
import './index.less'
import { ModalProps } from 'antd/lib/modal'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import PerButton, { isProcessingButton } from '../Button'
import { useTranslation } from 'react-i18next'
import { Colors } from '../../util/types'
import { icons } from '../Icon/icons'
import { Form } from '../../formik-component'
import PerButtonCircle from '../ButtonCircle'

interface CustomBtnsProps {
  text: string
  eventHandler?: Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: any
  asLink?: string
  color?: Colors
  icon?: keyof typeof icons
  disabled?: boolean
  tooltip?: string | ReactNode
  visible?: boolean
  type?: 'button' | 'submit'
  isProcessing?: isProcessingButton
  dataE2eAttr?: string
}
interface BaseConfirmBtn {
  eventHandler?: Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: any
  disabled?: boolean
  tooltip?: string | ReactNode
  visible?: boolean
  isProcessing?: isProcessingButton
}
interface ConfirmBtnWithText extends BaseConfirmBtn {
  text?: 'save' | 'confirm'
  altText?: never
}
interface ConfirmBtnWithAltText extends BaseConfirmBtn {
  text?: never
  altText: string
}
interface Footer {
  cancelBtn?: {
    onClick?: MouseEventHandler<HTMLButtonElement>
    icon?: keyof typeof icons
    text?: string
    visible?: boolean
  }
  confirmBtn?: ConfirmBtnWithText | ConfirmBtnWithAltText
  customBtns?: CustomBtnsProps[]
  formikForm?: {
    onSubmit?: FormEventHandler<HTMLFormElement>
    onReset?: unknown
  }
  justify?: 'start' | 'end' | 'space-between'
  hideButtons?: boolean
}
interface PerModalProps extends ModalProps {
  title: {
    heading?: string
    subheading?: string | ReactNode
    custom?: ReactNode
  }
  footer?: Footer
  size?: 'small' | 'large' | 'xlarge' | 'medium'
  borderRadius?: '5' | '10' | '15' | '20'
}

export const PerModal: FC<PerModalProps> = ({ title, size, borderRadius, footer, children, onCancel, closable, ...props }) => {
  const [t] = useTranslation()
  const base = 'per-modal'
  const modifiers: string[] = []

  if (size) {
    modifiers.push(`${base}--${size}`)
  }

  if (borderRadius) {
    modifiers.push(`${base}--radius-${borderRadius}`)
  }

  const renderFooterBtns = () => {
    const setJustify = (footer) => {
      if (footer.justify) {
        return footer.justify
      } else if (footer.cancelBtn && !footer.confirmBtn && !footer.customBtns) {
        return 'start'
      } else if (
        (footer.confirmBtn && !footer.cancelBtn && !footer.customBtns) ||
        (footer.customBtns && footer.customBtns.length === 1 && !footer.confirmBtn && !footer.cancelBtn && !footer.justify)
      ) {
        return 'end'
      } else {
        return 'space-between'
      }
    }

    const renderConfirmText = (confirmBtn: ConfirmBtnWithText | ConfirmBtnWithAltText) => {
      if (confirmBtn.text === 'save') {
        return t('global.button.save')
      } else if (confirmBtn.text === 'confirm') {
        return t('global.button.confirm')
      } else if (confirmBtn.altText) {
        return confirmBtn.altText
      } else {
        return t('global.button.confirm') // Fallback
      }
    }

    const renderBtns = (footer: Footer) => (
      <PerFlexRow justify={setJustify(footer)}>
        {footer.cancelBtn && (
          <PerButton
            text={footer.cancelBtn.text || t('global.button.cancel')}
            color="secondary"
            before={footer.cancelBtn.icon || 'close_stroke'}
            variant="pastel"
            type="button"
            onClick={footer.cancelBtn.onClick}
            data-e2e-modal-footer-button="cancel"
          />
        )}
        {footer.customBtns &&
          footer.customBtns.map((customBtn: CustomBtnsProps, index: number) => (
            <React.Fragment key={index}>
              {customBtn.visible !== false && (
                <PerButton
                  {...customBtn.eventHandler}
                  text={customBtn.text}
                  disabled={customBtn.disabled ? customBtn.disabled : false}
                  color={customBtn.color}
                  before={customBtn.icon}
                  variant="pastel"
                  type={customBtn.type ? customBtn.type : 'button'}
                  onClick={customBtn.onClick}
                  asLink={customBtn.asLink}
                  tooltip={customBtn.tooltip}
                  data-e2e-modal-footer-button={customBtn.dataE2eAttr ? customBtn.dataE2eAttr : 'true'}
                  isProcessing={customBtn.isProcessing}
                />
              )}
            </React.Fragment>
          ))}
        {footer.confirmBtn && footer.confirmBtn.visible !== false && (
          <PerButton
            text={renderConfirmText(footer.confirmBtn)}
            disabled={footer.confirmBtn.disabled ? footer.confirmBtn.disabled : false}
            color="success"
            before="check_stroke"
            variant="pastel"
            type="submit"
            {...footer.confirmBtn.eventHandler}
            onClick={footer.confirmBtn.onClick}
            tooltip={footer.confirmBtn.tooltip}
            isProcessing={footer.confirmBtn.isProcessing}
            data-e2e-modal-footer-button="confirm"
          />
        )}
      </PerFlexRow>
    )

    return (
      <>
        {footer && !footer.hideButtons && (
          <>{footer.formikForm ? <Form onSubmit={footer.formikForm.onSubmit}>{renderBtns(footer)}</Form> : footer && renderBtns(footer)}</>
        )}
      </>
    )
  }

  return (
    <Modal
      closable={closable ? closable : false}
      {...props}
      title={
        <>
          <PerFlexRow justify="space-between" align="middle" mb="none">
            <PerFlexRow mb="none">
              <PerText fontSize="xl" color="primary" mb="none">
                {title.custom || title.heading}
              </PerText>
            </PerFlexRow>
            <PerButtonCircle size="large" icon="close_stroke" color="transparent" onClick={onCancel} testid="tm-modal-close" />
          </PerFlexRow>
          {title.subheading && (
            <PerText color="secondary" fontSize="s">
              {title.subheading}
            </PerText>
          )}
        </>
      }
      footer={footer ? renderFooterBtns() : null}
      centered
      className={base + ' ' + modifiers.join(' ')}
    >
      {children}
    </Modal>
  )
}
